import { rpx } from 'client/lib/rpx-client';
import dayjs from 'dayjs';

type Member = Awaited<
  ReturnType<typeof rpx.engagementRetention.getMemberSignals>
>['members'][number];

type ActionKind = 'outcome' | 'note' | 'pause' | 'booking';

export function AipMemberCard(props: {
  member: Member;
  onAction: (m: Member, kind: ActionKind) => void;
  onOpen: (m: Member) => void;
}) {
  const m = props.member;
  const dollars = m.mrrCents !== undefined ? `$${(m.mrrCents / 100).toFixed(0)}/mo` : '—';
  const tenureLabel = formatTenure(m.tenureDays);
  const story = buildStory(m);
  const angle = suggestAngle(m);
  const last = m.lastCallAttendedAt ? dayjs(m.lastCallAttendedAt).fromNow() : 'never';
  const cancellationLine =
    m.cancellationStatus === 'canceling' || m.cancellationStatus === 'canceled'
      ? `${m.cancellationStatus === 'canceling' ? 'Canceling' : 'Cancelled'} ${m.cancelledAt ? dayjs(m.cancelledAt).fromNow() : ''}`
      : undefined;

  return (
    <article class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <header class="flex flex-wrap items-baseline justify-between gap-2">
        <button
          type="button"
          class="text-left text-base font-semibold text-indigo-700 hover:underline"
          onClick={() => props.onOpen(m)}
        >
          {m.name || m.email}
        </button>
        <div class="text-xs text-gray-500">
          {dollars} · joined {tenureLabel}
        </div>
      </header>

      {cancellationLine && (
        <p class="mt-1 text-sm font-medium text-amber-700">
          {cancellationLine}
          {m.cancellationReason ? <> · {humanizeReason(m.cancellationReason)}</> : null}
        </p>
      )}
      {m.cancellationNotes && (
        <p class="mt-1 text-sm text-gray-600 italic">"{m.cancellationNotes}"</p>
      )}

      <p class="mt-2 text-sm text-gray-700">
        {m.callsAttended} calls attended · last attended {last} · {m.postsCount} posts in last 90d
      </p>

      {m.acquisitionSource && (
        <p class="mt-1 text-xs text-gray-500">
          Source: <span class="font-medium text-gray-700">{m.acquisitionSource}</span>
        </p>
      )}
      <EmailEngagementLine bucket={m.emailEngagementBucket} lastEmail={m.opDateLastEmailReceived} />
      <YcbmLine
        count={m.ycbmCallsCount}
        lastBookedAt={m.ycbmLastBookedAt}
        lastStaffName={m.ycbmLastStaffName}
      />

      <p class="mt-3 text-sm text-gray-700">
        <span class="font-medium text-gray-900">Suggested angle:</span> {angle}
      </p>

      <div class="mt-3 flex flex-wrap gap-2">
        <ActionButton onClick={() => props.onAction(m, 'outcome')}>Log outcome</ActionButton>
        <ActionButton onClick={() => props.onAction(m, 'pause')}>Pause offer</ActionButton>
        <ActionButton onClick={() => props.onAction(m, 'booking')}>Send booking link</ActionButton>
        <ActionButton onClick={() => props.onAction(m, 'note')}>Note</ActionButton>
      </div>
      <div class="mt-2 text-xs text-gray-400">{story}</div>
    </article>
  );
}

function EmailEngagementLine(props: { bucket: Member['emailEngagementBucket']; lastEmail?: Date }) {
  if (props.bucket === 'unknown') {
    return null;
  }
  const labelMap: Record<Exclude<Member['emailEngagementBucket'], 'unknown'>, string> = {
    active_live: 'Live engaged',
    async_engaged: 'Recent email (30d)',
    drifting: 'Email drift (30-60d)',
    inactive: 'Email inactive (60d+)',
  };
  const colorMap: Record<Exclude<Member['emailEngagementBucket'], 'unknown'>, string> = {
    active_live: 'text-emerald-700',
    async_engaged: 'text-emerald-700',
    drifting: 'text-amber-700',
    inactive: 'text-gray-500',
  };
  const last = props.lastEmail ? dayjs(props.lastEmail).fromNow() : undefined;
  return (
    <p class={`mt-0.5 text-xs ${colorMap[props.bucket]}`}>
      {labelMap[props.bucket]}
      {last ? ` · last email ${last}` : ''}
    </p>
  );
}

function YcbmLine(props: { count: number; lastBookedAt?: Date; lastStaffName?: string }) {
  if (!props.count) {
    return null;
  }
  const last = props.lastBookedAt ? dayjs(props.lastBookedAt).fromNow() : undefined;
  return (
    <p class="mt-0.5 text-xs text-gray-500">
      {props.count} Mirasee {props.count === 1 ? 'call' : 'calls'} in last 120d
      {props.lastStaffName ? ` · last with ${props.lastStaffName}` : ''}
      {last ? ` ${last}` : ''}
    </p>
  );
}

function ActionButton(props: { onClick: () => void; children: preact.ComponentChildren }) {
  return (
    <button
      type="button"
      class="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
      onClick={props.onClick}
    >
      {props.children}
    </button>
  );
}

function formatTenure(days: number): string {
  if (days < 14) {
    return `${days} days ago`;
  }
  if (days < 90) {
    return `${Math.round(days / 7)} weeks ago`;
  }
  return `${Math.round(days / 30)} months ago`;
}

function buildStory(m: Member): string {
  const parts: string[] = [];
  if (m.cancellationStatus === 'canceled') {
    parts.push('Cancelled');
  } else if (m.cancellationStatus === 'canceling') {
    parts.push('Canceling');
  } else if (m.cancellationStatus === 'paused') {
    parts.push('Paused');
  }
  if (m.callsAttended > 0) {
    parts.push(`${m.callsAttended} calls`);
  }
  if (m.postsCount > 0) {
    parts.push(`${m.postsCount} posts`);
  }
  return parts.length ? parts.join(' · ') : 'No recent signals.';
}

function suggestAngle(m: Member): string {
  if (m.cancellationStatus === 'canceling' || m.cancellationStatus === 'canceled') {
    if (
      m.callsAttended >= 5 &&
      (m.cancellationReason === 'too-expensive' || m.cancellationReason === 'temporary')
    ) {
      return '60-day pause. Likely a return-when-cash-frees member.';
    }
    if (m.callsAttended <= 1 && m.cancellationReason === 'too-expensive') {
      return 'Probably let go cleanly. Low fit signal.';
    }
    if (m.callsAttended >= 3) {
      return 'Worth a save call. They were engaged.';
    }
    return 'Open-ended exit interview. Low engagement.';
  }
  if (m.callsAttended >= 3) {
    return 'Re-engagement nudge. Mention recent forum activity if any.';
  }
  if (m.trialDaysLeft !== undefined && m.trialDaysLeft > 0 && m.trialDaysLeft <= 7) {
    return 'Trial-end check-in. Help them book a call to see the value.';
  }
  return 'Open-ended check-in.';
}

function humanizeReason(r: string): string {
  switch (r) {
    case 'not-using':
      return 'Not using it';
    case 'too-expensive':
      return 'Price';
    case 'missing-features':
      return 'Missing features';
    case 'switching':
      return 'Switching';
    case 'temporary':
      return 'Time / life';
    case 'other':
      return 'Other';
    default:
      return r;
  }
}
